import { useEffect, useState } from 'react';

import { isRunningInNativeApp, native } from '@muzly/shared';
import type { NativeF0Frame } from '@muzly/shared';

import { InfoTip } from './InfoTip';
import { useFlashOnChange } from './useFlashOnChange';

/**
 * 리드아웃 패널 — 최신 `pitch_frame`(NativeF0Frame)의 f0/midi/note/confidence/
 * voiced/rms 카드. 세션 시작은 세션 제어 패널(#3) 소관이고, 이 패널은 동일한
 * `pitchTrackerEvent` push 스트림(native·브라우저 mock 공통)을 구독해 최신 프레임만 비춘다.
 *
 * 불변식: native payload는 필드를 누락/null로 줄 수 있다 — 포맷 함수는 렌더 중
 * 절대 throw하지 않는다(이 앱엔 error boundary가 없어 throw하면 화면이 통째로 빈다).
 */

// ── 순수 포맷 함수(렌더 무관, vitest 대상) ────────────────────────────────

/** NaN/null/누락 숫자 안전 포맷. 기존 PitchTestPage `fixed()` 패턴 재사용. */
export const fixed = (value: unknown, digits: number, suffix = ''): string =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${value.toFixed(digits)}${suffix}`
    : '—';

/** 정수 midi(0~127)는 소수점 없이, null/비유한이면 '—'. */
export const formatMidi = (midi: number | null | undefined): string =>
  typeof midi === 'number' && Number.isFinite(midi) ? `${midi}` : '—';

/** note는 문자열 그대로, null/누락이면 '—'. */
export const formatNote = (note: string | null | undefined): string =>
  note ?? '—';

/** voiced 3-state: true→yes, false→no, null/누락→'—'. */
export const formatVoiced = (voiced: boolean | null | undefined): string =>
  voiced == null ? '—' : voiced ? 'yes' : 'no';

// ── 렌더 ─────────────────────────────────────────────────────────────────

interface ReadoutCardProps {
  label: string;
  value: string;
  /** "?" 툴팁: 이 지표가 무엇인지 KO 평이체 한 줄(#16). */
  tip: string;
  /** voiced=false 등 비활성 값은 흐리게. */
  muted?: boolean;
}

const ReadoutCard = ({ label, value, tip, muted }: ReadoutCardProps) => {
  // 값이 갱신될 때마다 key가 바뀌어 하이라이트 펄스를 재생한다(#23).
  const flashKey = useFlashOnChange(value);

  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-border bg-card p-2">
      <span className="flex items-center gap-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <InfoTip label={`${label} 설명`}>{tip}</InfoTip>
      </span>
      <span
        className={`font-mono text-sm ${muted ? 'text-muted-foreground' : 'text-foreground'}`}
      >
        <span
          key={flashKey}
          className={`-mx-1 inline-block rounded px-1 ${flashKey ? 'flash-pulse' : ''}`}
        >
          {value}
        </span>
      </span>
    </div>
  );
};

export const ReadoutPanel = () => {
  const [frame, setFrame] = useState<NativeF0Frame | null>(null);

  // pitchTrackerEvent 구독: pitch_frame은 최신 프레임 갱신, 세션 종료/에러는 리셋.
  useEffect(
    () =>
      native.on('pitchTrackerEvent', (event) => {
        if (event.type === 'pitch_frame') {
          setFrame(event.frame ?? null);
          return;
        }
        if (event.type === 'session_stopped' || event.type === 'error') {
          setFrame(null);
        }
      }),
    [],
  );

  const isVoiced = frame?.voiced === true;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        <ReadoutCard
          label="f0"
          value={fixed(frame?.f0Hz, 1, ' Hz')}
          tip="기본 주파수(음높이)를 헤르츠(Hz)로 나타냅니다. 숫자가 클수록 높은 음입니다."
          muted={!isVoiced}
        />
        <ReadoutCard
          label="midi"
          value={formatMidi(frame?.midi)}
          tip="음높이를 MIDI 번호(0~127)로 환산한 값입니다. 60이 가운데 도(C4), 1 차이가 반음입니다."
          muted={!isVoiced}
        />
        <ReadoutCard
          label="note"
          value={formatNote(frame?.note)}
          tip="가장 가까운 음이름입니다(예: A4 = 라). 옆 숫자는 옥타브입니다."
          muted={!isVoiced}
        />
        <ReadoutCard
          label="confidence"
          value={fixed(frame?.confidence, 2)}
          tip="음높이 추정의 신뢰도(0~1)입니다. 1에 가까울수록 확실하고, 낮으면 잡음일 수 있습니다."
        />
        <ReadoutCard
          label="voiced"
          value={formatVoiced(frame?.voiced)}
          tip="실제 소리(목소리)가 있는지 여부입니다. yes면 음을 내는 중, no면 무음입니다."
        />
        <ReadoutCard
          label="rms"
          value={fixed(frame?.rmsDb, 1, ' dB')}
          tip="입력 음량을 dBFS로 나타냅니다. 0이 최대, 값이 작을수록(예: −60) 조용합니다."
        />
      </div>

      {!frame && (
        <p className="rounded-md bg-muted/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
          시작을 누르면 여기에 음정이 표시됩니다.
          {isRunningInNativeApp()
            ? ' 값이 안 나오면 마이크 권한·입력 장치를 확인하세요.'
            : ' (데모 데이터 모드 — 실제 마이크 입력이 아닙니다.)'}
        </p>
      )}
    </div>
  );
};
